import { useState, type FC, type FormEvent } from 'react';
import { SETTING_FIELDS } from '../../util/Constants';
import type { ToastFn, ZohoInfo, ZohoSettings } from '../../types/integrations';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}

function ToggleSwitch({ checked, onChange, label }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

interface SettingsTabProps {
  info: ZohoInfo;
  onSaved: () => void;
  toast: ToastFn;
}

const SettingsTab: FC<SettingsTabProps> = ({ info, onSaved, toast }) => {
  const [s, setS] = useState<ZohoSettings>(info.settings ?? {});
  const [busy, setBusy] = useState(false);

  if (info.status === 'NOT_CONNECTED') {
    return (
      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
        Connect first to change settings.
      </div>
    );
  }

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    try {
      // await api.saveSettings('ZOHO', s);
      toast('Settings saved');
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Save failed', 'bad');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="bg-white rounded-xl border border-slate-200 p-5 max-w-lg flex flex-col gap-4"
      onSubmit={save}
    >
      {SETTING_FIELDS.map(([k, label, hint, isFlag]) =>
        isFlag ? (
          <label key={hint} className="flex items-center gap-2">
            <ToggleSwitch
              checked={Boolean(s[k as keyof ZohoSettings])}
              label={label}
              onChange={(v) => setS({ ...s, [k]: v })}
            />
            <span className="text-sm text-slate-700">{label}</span>
          </label>
        ) : (
          <label key={k} className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">{label}</span>
            <input
              value={(s[k as keyof ZohoSettings] as string) ?? ''}
              onChange={(e) => setS({ ...s, [k]: e.target.value })}
              className={inputCls}
            />
            {hint && <span className="text-xs text-slate-500">{hint}</span>}
          </label>
        )
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </form>
  );
};

export default SettingsTab;